import { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppContext } from "@/App";
import api from "@/lib/api";
import { Star, Crown, Loader2, CheckCircle2, XCircle } from "lucide-react";

const STRINGS = {
  it: {
    title: "Come è andata?",
    with: "la tua corsa",
    placeholder: "Racconta la tua esperienza: puntualità, auto, autista…",
    submit: "Invia recensione",
    thanks: "Grazie di cuore!",
    thanksSub: "La tua recensione è pubblicata sul nostro sito.",
    error: "Link non valido o recensione già inviata.",
    back: "Torna al sito",
  },
  en: {
    title: "How was it?",
    with: "your ride",
    placeholder: "Tell us about your experience: punctuality, car, chauffeur…",
    submit: "Submit review",
    thanks: "Thank you!",
    thanksSub: "Your review is now published on our website.",
    error: "Invalid link or review already submitted.",
    back: "Back to site",
  },
};

export default function Review() {
  const { bookingId } = useParams();
  const { lang } = useContext(AppContext);
  const s = STRINGS[lang];
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [state, setState] = useState("form");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/review-info/${bookingId}`)
      .then((r) => {
        if (r.data.already_reviewed) setState("error");
        else setInfo(r.data);
      })
      .catch(() => setState("error"));
  }, [bookingId]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/reviews", { booking_id: bookingId, rating, text });
      setState("done");
    } catch {
      setState("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0C0E] px-4">
      <div className="card-surface w-full max-w-md p-8 text-center" data-testid="review-page">
        <Crown className="mx-auto mb-4 h-8 w-8 text-[#D4AF37]" />
        {state === "form" && (
          <>
            <h1 className="font-display text-3xl font-semibold text-white">{s.title}</h1>
            {info ? (
              <p className="mt-2 text-sm text-neutral-400">
                {info.name} · {info.pickup} → {info.dropoff} · {info.date}
              </p>
            ) : (
              <Loader2 className="mx-auto mt-4 h-6 w-6 animate-spin text-[#D4AF37]" />
            )}
            <div className="mt-6 flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`star-${n}`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className="transition-transform duration-150 hover:scale-125"
                >
                  <Star
                    className={`h-9 w-9 ${(hover || rating) >= n ? "fill-[#D4AF37] text-[#D4AF37]" : "text-neutral-600"}`}
                  />
                </button>
              ))}
            </div>
            <textarea
              data-testid="review-text"
              className="input-dark mt-6 min-h-[110px] resize-none"
              placeholder={s.placeholder}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <button data-testid="submit-review-btn" onClick={submit} disabled={loading || !info} className="btn-gold mt-5 w-full">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {s.submit}
            </button>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-400" />
            <h1 className="font-display text-3xl font-semibold text-white">{s.thanks}</h1>
            <p className="mt-2 text-sm text-neutral-400">{s.thanksSub}</p>
            <Link to="/" className="btn-gold mt-6">{s.back}</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-neutral-500" />
            <p className="text-neutral-300">{s.error}</p>
            <Link to="/" className="btn-outline-gold mt-6">{s.back}</Link>
          </>
        )}
      </div>
    </div>
  );
}
